import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PlaceIcon from "@mui/icons-material/Place";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { schedulingApi } from "../Components/schedulingApi";
import { toast } from "react-toastify";
import styles from "./CreateEnquiryWizard.module.scss";

const TOTAL_STEPS = 4;

const DEFAULT_FORM_DATA = {
  room_code: "",
  capacity: 1,
  building: "",
  floor: "",
  equipment: "",
  description: "",
  is_active: true,
};

const STEP_META = [
  { id: 1, short: "Basics", title: "Room & Capacity", icon: <MeetingRoomIcon fontSize="small" /> },
  { id: 2, short: "Location", title: "Building & Floor", icon: <PlaceIcon fontSize="small" /> },
  { id: 3, short: "Details", title: "Equipment & Notes", icon: <InfoOutlinedIcon fontSize="small" /> },
  { id: 4, short: "Review", title: "Review & Submit", icon: <PreviewOutlinedIcon fontSize="small" /> },
];

const STEP_FIELDS = {
  1: ["room_code", "capacity"],
  2: ["building", "floor", "equipment"],
  3: ["description", "is_active"],
};

export default function CreateClassroomWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdId, setCreatedId] = useState("");

  const progressPercent = (currentStep / TOTAL_STEPS) * 100;

  const validateStep1 = (data = formData) => {
    const nextErrors = {};
    const code = (data.room_code || "").trim();
    if (!code) nextErrors.room_code = "Room code is required.";
    const cap = Number(data.capacity);
    if (!Number.isInteger(cap) || cap < 1) nextErrors.capacity = "Capacity must be at least 1.";
    return nextErrors;
  };

  const applyStepErrors = (step, stepErrors) => {
    const stepFields = STEP_FIELDS[step] || [];
    setErrors((prev) => {
      const next = { ...prev };
      stepFields.forEach((f) => delete next[f]);
      return { ...next, ...stepErrors };
    });
  };

  const setFieldValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (STEP_FIELDS[1].includes(field)) setErrors((prev) => ({ ...prev, [field]: null }));
    if (STEP_FIELDS[2].includes(field)) setErrors((prev) => ({ ...prev, [field]: null }));
    if (STEP_FIELDS[3].includes(field)) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const goToStep = (step) => setCurrentStep(step);

  const handleNext = () => {
    if (currentStep === 1) {
      const stepErrors = validateStep1();
      applyStepErrors(1, stepErrors);
      if (Object.keys(stepErrors).length > 0) return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isSubmitting) return;

    const step1Errors = validateStep1();
    applyStepErrors(1, step1Errors);
    if (Object.keys(step1Errors).length > 0) {
      setCurrentStep(1);
      return;
    }

    setApiError("");
    setIsSubmitting(true);
    try {
      const payload = {
        room_code: formData.room_code.trim(),
        capacity: Number(formData.capacity) || 1,
        building: formData.building || null,
        floor: formData.floor || null,
        equipment: formData.equipment || null,
        description: formData.description || null,
        is_active: Boolean(formData.is_active),
      };
      const data = await schedulingApi.createClassroom(payload);
      if (data?.error) {
        setApiError(data.error);
        return;
      }
      setCreatedId(data.classroom?.id ?? data.classroom?.room_code ?? "—");
      setShowSuccessModal(true);
      toast.success("Classroom created.");
    } catch (err) {
      setApiError(err?.message || "Failed to create classroom.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetWizard = () => {
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
    setCurrentStep(1);
    setShowSuccessModal(false);
    setCreatedId("");
  };

  const renderFieldError = (field) =>
    errors[field] ? (
      <div className={styles.errorText} role="alert">
        {errors[field]}
      </div>
    ) : null;

  return (
    <Container fluid className={`${styles.page} d-flex justify-content-center align-items-center py-4`}>
      <div className={styles.wizardWrap}>
            <Card className={styles.wizardCard}>
              <Card.Body className={styles.cardBody}>
                <Button type="button" variant="link" className={styles.gwBackBtn} onClick={() => navigate("/schedule/classrooms")}>
                  ← Back
                </Button>
                <header className={styles.gwHeader}>
                  <div className={styles.gwTitleSection}>
                    <h2 className={styles.gwTitle}>Create Classroom</h2>
                    <p className={styles.gwSubtitle}>Add a new room in a few steps.</p>
                  </div>
                  <div className={styles.gwProgressIndicator}>
                    <div className={styles.gwProgressText}>Step {currentStep} of {TOTAL_STEPS}</div>
                    <ProgressBar now={progressPercent} className={styles.gwLinearProgress} aria-label={`Step ${currentStep} of ${TOTAL_STEPS}`} />
                  </div>
                </header>

                <nav className={styles.gwNav} aria-label="Wizard steps">
                  <div className={styles.gwProgress}>
                    {STEP_META.map((step, idx) => {
                      const stepNumber = idx + 1;
                      const status = stepNumber < currentStep ? "done" : stepNumber === currentStep ? "current" : "locked";
                      const stepClass = status === "done" ? styles.gwStepDone : status === "current" ? styles.gwStepCurrent : styles.gwStepLocked;
                      const badgeClass = status === "done" ? styles.gwStepBadgeDone : status === "current" ? styles.gwStepBadgeCurrent : styles.gwStepBadgeLocked;
                      const shortClass = status === "done" ? styles.gwStepShortDone : status === "current" ? styles.gwStepShortCurrent : styles.gwStepShortLocked;
                      return (
                        <button
                          key={step.id}
                          type="button"
                          className={`${styles.gwStep} ${stepClass}`}
                          onClick={() => goToStep(stepNumber)}
                          disabled={stepNumber > currentStep}
                          aria-label={`Go to step ${stepNumber}: ${step.title}`}
                        >
                          <div className={`${styles.gwStepBadge} ${badgeClass}`}>
                            {status === "done" ? <CheckCircleRoundedIcon fontSize="small" /> : step.icon}
                          </div>
                          <div className={styles.gwStepLabel}>
                            <div className={`${styles.gwStepShort} ${shortClass}`}>{step.short}</div>
                            <div className={styles.gwStepTitle}>{step.title}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </nav>

                {apiError && <Alert variant="danger">{apiError}</Alert>}

                <Form onSubmit={(e) => e.preventDefault()} noValidate>
                  <section key={currentStep} className={styles.stepBody} aria-live="polite">
                    {currentStep === 1 && (
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group controlId="room_code">
                            <Form.Label>Room code *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.room_code}
                              onChange={(e) => setFieldValue("room_code", e.target.value)}
                              isInvalid={Boolean(errors.room_code)}
                              placeholder="e.g. R101"
                            />
                            {renderFieldError("room_code")}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="capacity">
                            <Form.Label>Capacity *</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={formData.capacity}
                              onChange={(e) => setFieldValue("capacity", e.target.value)}
                              isInvalid={Boolean(errors.capacity)}
                            />
                            {renderFieldError("capacity")}
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 2 && (
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group controlId="building">
                            <Form.Label>Building</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.building}
                              onChange={(e) => setFieldValue("building", e.target.value)}
                              placeholder="Building name"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group controlId="floor">
                            <Form.Label>Floor</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.floor}
                              onChange={(e) => setFieldValue("floor", e.target.value)}
                              placeholder="e.g. 1"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group controlId="equipment">
                            <Form.Label>Equipment</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.equipment}
                              onChange={(e) => setFieldValue("equipment", e.target.value)}
                              placeholder="e.g. Projector, Whiteboard"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 3 && (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Group controlId="description">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={formData.description}
                              onChange={(e) => setFieldValue("description", e.target.value)}
                              placeholder="Optional notes about the room"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group>
                            <Form.Check
                              type="checkbox"
                              id="is_active"
                              label="Active (room available for booking)"
                              checked={formData.is_active}
                              onChange={(e) => setFieldValue("is_active", e.target.checked)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {currentStep === 4 && (
                      <div className={styles.reviewWrap}>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Room & Capacity</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(1)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Room code</span>
                                <span className={styles.reviewValue}>{formData.room_code || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Capacity</span>
                                <span className={styles.reviewValue}>{formData.capacity}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Location</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(2)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Building</span>
                                <span className={styles.reviewValue}>{formData.building || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Floor</span>
                                <span className={styles.reviewValue}>{formData.floor || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Equipment</span>
                                <span className={styles.reviewValue}>{formData.equipment || "—"}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className={styles.reviewCard}>
                          <Card.Body>
                            <div className={styles.reviewHeader}>
                              <h5>Details</h5>
                              <Button type="button" variant="outline-primary" size="sm" onClick={() => goToStep(3)}>Edit</Button>
                            </div>
                            <div className={styles.reviewGrid}>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Description</span>
                                <span className={styles.reviewValue}>{formData.description || "—"}</span>
                              </div>
                              <div className={styles.reviewItem}>
                                <span className={styles.reviewLabel}>Active</span>
                                <span className={styles.reviewValue}>{formData.is_active ? "Yes" : "No"}</span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                  </section>

                  <div className={styles.actions}>
                    <Button type="button" variant="outline-secondary" onClick={handleBack} disabled={currentStep === 1 || isSubmitting}>
                      Back
                    </Button>
                    {currentStep < TOTAL_STEPS ? (
                      <Button type="button" variant="primary" onClick={handleNext} disabled={isSubmitting}>
                        Next
                      </Button>
                    ) : (
                      <Button variant="primary" type="button" onClick={handleSubmit} disabled={isSubmitting} className={styles.submitBtn}>
                        {isSubmitting ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span className={styles.loadingText}>Creating...</span>
                          </>
                        ) : (
                          "Create Classroom"
                        )}
                      </Button>
                    )}
                  </div>
                </Form>
              </Card.Body>
            </Card>
      </div>

      <Modal show={showSuccessModal} onHide={resetWizard} centered>
        <Modal.Header closeButton>
          <Modal.Title>Classroom Created</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.successModalBody}>
          <div className={styles.successIcon} aria-hidden="true" />
          <p className={styles.successText}>Classroom created successfully</p>
          <p className={styles.successId}>
            Room: <strong>{formData.room_code}</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => navigate("/schedule/classrooms")}>
            Back to list
          </Button>
          <Button variant="primary" onClick={resetWizard}>
            Create another
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
